import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as productActions from "../store/action/product";
import { useParams, useLocation } from "react-router-dom";
import { throttle } from "lodash";

export const useSellerProductFetch = (type) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { sellerPagination } = useSelector((state) => state.product);
  const { vendor_id } = useParams();
  const { storeCity } = useSelector((state) => state.auth);
  const location = useLocation();

  // Store refs for values that need to be fresh in callbacks
  const storeCityRef = useRef(storeCity);
  const vendorIdRef = useRef(vendor_id);
  const typeRef = useRef(type);

  // Keep refs updated
  useEffect(() => {
    storeCityRef.current = storeCity;
    vendorIdRef.current = vendor_id;
    typeRef.current = type;
  }, [storeCity, vendor_id, type]);

  // Store previous values to compare changes
  const prevParamsRef = useRef({
    storeCity,
    vendor_id,
    type,
    pathname: location.pathname,
  });

  const isInitialLoad = useRef(true);

  const sellerProductFetch = useCallback(
    async (newPage = 1, force = false) => {
      const currentVendorId = vendorIdRef.current;
      const currentStoreCity = storeCityRef.current;
      const currentType = typeRef.current;

      if ((isLoading && !force) || !currentVendorId) {
        return;
      }

      console.log("Fetching products with city:", currentStoreCity);

      setIsLoading(true);
      try {
        await dispatch(
          productActions.getSellerWiseProducts(
            currentVendorId,
            currentStoreCity,
            currentType,
            newPage
          )
        );
      } catch (error) {
        console.log("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, isLoading]
  );

  // Update pagination info when sellerPagination changes
  useEffect(() => {
    if (sellerPagination) {
      setHasMore(page < sellerPagination.totalPages);
    }
  }, [sellerPagination, page]);

  // Handle route changes and initial load
  useEffect(() => {
    const paramsChanged =
      prevParamsRef.current.vendor_id !== vendor_id ||
      prevParamsRef.current.type !== type ||
      prevParamsRef.current.storeCity !== storeCity ||
      prevParamsRef.current.pathname !== location.pathname;

    if (!isInitialLoad.current && !paramsChanged) return;

    if (
      prevParamsRef.current.vendor_id !== vendor_id ||
      prevParamsRef.current.type !== type ||
      prevParamsRef.current.storeCity !== storeCity
    ) {
      setPage(1);
      setHasMore(true);
    }

    sellerProductFetch(page, true);
    prevParamsRef.current = {
      storeCity,
      vendor_id,
      type,
      pathname: location.pathname,
    };
    isInitialLoad.current = false;
  }, [vendor_id, type, location.pathname, sellerProductFetch, page, storeCity]);

  // Scroll handler with cleanup
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (isLoading || !hasMore) return;

      const scrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const pageHeight = document.documentElement.offsetHeight;
      const threshold = 500;

      if (pageHeight - scrollPosition < threshold) {
        const nextPage = page + 1;
        setPage(nextPage);
        sellerProductFetch(nextPage);
      }
    }, 500);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [isLoading, hasMore, page, sellerProductFetch]);

  return { isLoading, hasMore };
};
